/**
 * 공용 axios 인스턴스.
 *
 * 책임:
 *   1. baseURL("/api") 와 기본 헤더 설정
 *   2. request 인터셉터로 access token 자동 첨부
 *   3. response 401 → refresh 토큰으로 재시도, 실패 시 강제 로그아웃
 *   4. 동시 다발 401 발생 시 refresh 1회만 호출 (큐로 직렬화)
 */

import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from "axios";

import { useAuthStore } from "@/features/auth/store";

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public raw?: unknown,
  ) {
    super(detail || `HTTP ${status}`);
    this.name = "ApiError";
  }
}

export const apiClient = axios.create({
  baseURL: "/api",
  timeout: 60_000,
  headers: {
    "Content-Type": "application/json",
  },
});

// ────────────── Request: Authorization 자동 첨부 ──────────────
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ────────────── Response: 401 → refresh 1회 후 재시도 ──────────────

type RetriableConfig = AxiosRequestConfig & { _retried?: boolean };

let refreshing: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  const { refreshToken, setTokens, clear } = useAuthStore.getState();
  if (!refreshToken) {
    clear();
    return null;
  }
  try {
    // refresh 엔드포인트는 자체 인스턴스로 호출 — 재귀 인터셉터 회피
    const res = await axios.post<{
      access_token: string;
      refresh_token: string;
      token_type: string;
    }>("/api/auth/refresh", { refresh_token: refreshToken });
    setTokens(res.data.access_token, res.data.refresh_token);
    return res.data.access_token;
  } catch {
    clear();
    return null;
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    // 401 + 아직 재시도 안 한 요청 → refresh 시도
    if (status === 401 && original && !original._retried) {
      original._retried = true;

      // 동시 401 가 와도 refresh 는 1회만
      refreshing = refreshing ?? performRefresh();
      const newToken = await refreshing;
      refreshing = null;

      if (newToken) {
        original.headers = {
          ...(original.headers ?? {}),
          Authorization: `Bearer ${newToken}`,
        };
        return apiClient.request(original);
      }
    }

    // 표준화된 에러 객체로 변환
    const detail =
      (error.response?.data as { detail?: string } | undefined)?.detail ?? error.message;
    return Promise.reject(new ApiError(status ?? 0, detail, error.response?.data));
  },
);
